import type { ForecastSeries } from "@kmitl/shared";

interface Props {
  series: ForecastSeries;
  height?: number;
  accentColor?: string;
  showLabels?: boolean;
}

function aqiColor(v: number): string {
  if (v <= 50) return "var(--good)";
  if (v <= 100) return "#FACC15";
  if (v <= 150) return "#FF7E00";
  if (v <= 200) return "var(--bad)";
  return "#7E0023";
}

export function ForecastSparkline({ series, height = 48, accentColor, showLabels = false }: Props) {
  const { points, metric, unit, model } = series;
  if (points.length < 2) return null;

  const W = 240;
  const H = height;
  const PAD = 2;
  const plotW = W - PAD * 2;
  const plotH = H - PAD * 2;

  const allVals = points.flatMap((p) => [p.low, p.high]);
  const minV = Math.min(...allVals);
  const maxV = Math.max(...allVals);
  const range = maxV - minV || 1;

  const xOf = (i: number) => PAD + (i / (points.length - 1)) * plotW;
  const yOf = (v: number) => PAD + plotH - ((v - minV) / range) * plotH;

  // Confidence band path (high → low reversed)
  const highPts = points.map((p, i) => `${i === 0 ? "M" : "L"}${xOf(i).toFixed(1)},${yOf(p.high).toFixed(1)}`).join(" ");
  const lowPtsRev = [...points].reverse().map((p, i) => `L${xOf(points.length - 1 - i).toFixed(1)},${yOf(p.low).toFixed(1)}`).join(" ");
  const bandPath = `${highPts} ${lowPtsRev} Z`;

  // Mean line path
  const meanPath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${xOf(i).toFixed(1)},${yOf(p.value).toFixed(1)}`)
    .join(" ");

  const color = accentColor ?? (metric === "aqi" ? aqiColor(points[points.length - 1]?.value ?? 60) : "var(--data)");

  // Label every 6h
  const labelIndices = points.reduce<number[]>((acc, _, i) => {
    if (i % 6 === 0) acc.push(i);
    return acc;
  }, []);

  const modelLabel = model === "timefm" ? "TimeFM" : model === "open-meteo" ? "Open-Meteo" : "Synthetic";

  return (
    <div className="forecast-sparkline">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} aria-label={`${metric} 24h forecast`}>
        {/* Confidence band */}
        <path d={bandPath} fill={color} fillOpacity={0.12} stroke="none" />
        {/* Mean line */}
        <path d={meanPath} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        {/* Now marker */}
        <line x1={PAD} y1={PAD} x2={PAD} y2={H - PAD} stroke="var(--text-3)" strokeWidth={0.75} strokeDasharray="2 2" />
        {/* Hour labels */}
        {showLabels && labelIndices.map((i) => (
          <text key={i} x={xOf(i)} y={H} fontSize={7} fill="var(--text-3)" textAnchor="middle" fontFamily="var(--font-mono)">
            +{i + 1}h
          </text>
        ))}
      </svg>
      <div className="forecast-sparkline-meta mono">
        <span className="forecast-sparkline-range">
          {Math.round(Math.min(...points.map((p) => p.value)))}–{Math.round(Math.max(...points.map((p) => p.value)))} {unit}
        </span>
        <span className="forecast-sparkline-model" style={{ color: model === "timefm" ? "var(--data)" : "var(--text-3)" }}>
          {modelLabel}
        </span>
      </div>
    </div>
  );
}
